import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Upload, Eye, Plus, X } from 'lucide-react';
import {
  getPlatformBranding,
  uploadPlatformLogo,
  getInvoiceTemplate,
  updateInvoiceTemplate,
  PLATFORM_LOGO_URL,
  type InvoiceTemplate,
} from '../../api/platformBranding';
import { getPlatformDefaults, updatePlatformDefaults } from '../../api/platformDefaults';
import { listPlatformCategories, createPlatformCategory, deletePlatformCategory } from '../../api/platformCategories';
import { printInvoice, buildPreviewInvoice } from '../../lib/invoiceTemplate';
import { apiErrorMessage } from '../../lib/api';
import { useToast } from '../../components/ui/Toast';
import { Card, CardBody, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input, FieldLabel } from '../../components/ui/Input';
import { Alert } from '../../components/ui/Alert';
import { PageHeader } from '../../components/ui/PageHeader';
import { Spinner } from '../../components/ui/Spinner';

// §21: platform-wide category list - the master catalog item dropdown's
// options, and the seed list every new site's own category list starts
// from (see backend SitesService.create). Same chip-list pattern as
// SiteCategoriesModal, just without an org/site to scope to.
function CategoriesSection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const categoriesQuery = useQuery({ queryKey: ['platform-categories'], queryFn: listPlatformCategories });

  const addMutation = useMutation({
    mutationFn: () => createPlatformCategory(name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-categories'] });
      setName('');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not add the category.')),
  });

  const removeMutation = useMutation({
    mutationFn: (categoryId: string) => deletePlatformCategory(categoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-categories'] });
      showToast('Category removed.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not remove the category.')),
  });

  function handleAdd(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) return;
    addMutation.mutate();
  }

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">
          Offered when adding or editing a master catalog item, and seeded onto every new site's own category
          list. Removing one here doesn't change items already tagged with it.
        </span>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}

        {categoriesQuery.isLoading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {(categoriesQuery.data ?? []).map((c) => (
              <span
                key={c.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 py-1 pl-3 pr-1.5 text-sm text-slate-700"
              >
                {c.name}
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    removeMutation.mutate(c.id);
                  }}
                  className="rounded-full p-0.5 text-slate-400 hover:bg-slate-200 hover:text-red-600"
                  aria-label={`Remove ${c.name}`}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
            {(categoriesQuery.data ?? []).length === 0 && (
              <span className="text-sm text-slate-400">No categories yet.</span>
            )}
          </div>
        )}

        <form onSubmit={handleAdd} className="flex items-end gap-2">
          <Input
            label="Add category"
            className="w-56"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Monitor"
          />
          <Button type="submit" size="sm" variant="secondary" isLoading={addMutation.isPending}>
            <Plus className="h-3.5 w-3.5" /> Add
          </Button>
        </form>
      </CardBody>
    </Card>
  );
}

function DefaultsSection() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [vatRate, setVatRate] = useState('20');
  const [error, setError] = useState<string | null>(null);

  const defaultsQuery = useQuery({ queryKey: ['platform-defaults'], queryFn: getPlatformDefaults });

  useEffect(() => {
    if (defaultsQuery.data) setVatRate(defaultsQuery.data.defaultVatRate);
  }, [defaultsQuery.data]);

  const mutation = useMutation({
    mutationFn: () => updatePlatformDefaults(Number(vatRate)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-defaults'] });
      showToast('Platform defaults updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update platform defaults.')),
  });

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">
          Applied to every new customer organisation when it's created. Existing customers keep their own VAT
          rate, editable from their Settings tab.
        </span>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        {defaultsQuery.isLoading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : (
          <div className="flex items-end gap-3">
            <Input
              label="Default VAT rate (%)"
              type="number"
              min="0"
              max="100"
              step="0.1"
              className="w-40"
              value={vatRate}
              onChange={(e) => setVatRate(e.target.value)}
            />
            <Button
              size="sm"
              isLoading={mutation.isPending}
              disabled={vatRate === defaultsQuery.data?.defaultVatRate}
              onClick={() => {
                setError(null);
                mutation.mutate();
              }}
            >
              Save
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
}

const TEXT_FIELDS: { key: keyof InvoiceTemplate; label: string; placeholder?: string }[] = [
  { key: 'companyName', label: 'Company name', placeholder: 'Oakford Technology' },
  { key: 'addressLine1', label: 'Address line 1' },
  { key: 'addressLine2', label: 'Address line 2' },
  { key: 'city', label: 'City' },
  { key: 'postcode', label: 'Postcode' },
  { key: 'country', label: 'Country' },
  { key: 'vatNumber', label: 'VAT number' },
  { key: 'registrationNumber', label: 'Company registration number' },
];

type TemplateFormState = Record<Exclude<keyof InvoiceTemplate, 'hasLogo' | 'updatedAt'>, string>;

const EMPTY_TEMPLATE_FORM: TemplateFormState = {
  companyName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: '',
  vatNumber: '',
  registrationNumber: '',
  bankDetails: '',
  footerNote: '',
  accentColor: '',
};

// §116: invoice template designer - everything printed on the invoice
// letterhead besides the logo (which is set above, in the section this
// sits below). "Preview" renders the exact same HTML a real invoice would
// use (see lib/invoiceTemplate.ts) against dummy line items and whatever's
// currently in the form, so an MSP Admin can iterate without needing to
// save first or find a real invoice to test against.
function InvoiceTemplateSection({ hasLogo }: { hasLogo: boolean }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [form, setForm] = useState<TemplateFormState>(EMPTY_TEMPLATE_FORM);
  const [error, setError] = useState<string | null>(null);

  const templateQuery = useQuery({ queryKey: ['invoice-template'], queryFn: getInvoiceTemplate });

  useEffect(() => {
    if (!templateQuery.data) return;
    const t = templateQuery.data;
    setForm({
      companyName: t.companyName ?? '',
      addressLine1: t.addressLine1 ?? '',
      addressLine2: t.addressLine2 ?? '',
      city: t.city ?? '',
      postcode: t.postcode ?? '',
      country: t.country ?? '',
      vatNumber: t.vatNumber ?? '',
      registrationNumber: t.registrationNumber ?? '',
      bankDetails: t.bankDetails ?? '',
      footerNote: t.footerNote ?? '',
      accentColor: t.accentColor ?? '#1d4ed8',
    });
  }, [templateQuery.data]);

  function set(key: keyof TemplateFormState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  const saveMutation = useMutation({
    // Explicit null (not undefined) for a blanked-out field - undefined
    // would be dropped entirely by JSON serialization, so clearing a field
    // in the form and hitting Save would silently leave the old value in
    // place server-side instead of actually clearing it.
    mutationFn: () =>
      updateInvoiceTemplate({
        companyName: form.companyName || null,
        addressLine1: form.addressLine1 || null,
        addressLine2: form.addressLine2 || null,
        city: form.city || null,
        postcode: form.postcode || null,
        country: form.country || null,
        vatNumber: form.vatNumber || null,
        registrationNumber: form.registrationNumber || null,
        bankDetails: form.bankDetails || null,
        footerNote: form.footerNote || null,
        accentColor: form.accentColor || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoice-template'] });
      showToast('Invoice template updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not update the invoice template.')),
  });

  function handlePreview() {
    try {
      printInvoice(buildPreviewInvoice(), {
        hasLogo,
        companyName: form.companyName || null,
        addressLine1: form.addressLine1 || null,
        addressLine2: form.addressLine2 || null,
        city: form.city || null,
        postcode: form.postcode || null,
        country: form.country || null,
        vatNumber: form.vatNumber || null,
        registrationNumber: form.registrationNumber || null,
        bankDetails: form.bankDetails || null,
        footerNote: form.footerNote || null,
        accentColor: form.accentColor || null,
        updatedAt: new Date().toISOString(),
      });
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Could not open the preview window.', 'error');
    }
  }

  return (
    <Card>
      <CardHeader>
        <span className="text-sm font-medium text-slate-600">
          What's printed on every customer's invoice, alongside the logo above.
        </span>
        <Button size="sm" variant="secondary" onClick={handlePreview}>
          <Eye className="h-3.5 w-3.5" /> Preview
        </Button>
      </CardHeader>
      <CardBody className="flex flex-col gap-4">
        {error && <Alert tone="error">{error}</Alert>}
        {templateQuery.isLoading ? (
          <div className="py-6 text-center">
            <Spinner />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              {TEXT_FIELDS.map((f) => (
                <Input key={f.key} label={f.label} placeholder={f.placeholder} value={form[f.key as keyof TemplateFormState]} onChange={(e) => set(f.key as keyof TemplateFormState, e.target.value)} />
              ))}
            </div>

            <div className="flex items-center gap-3">
              <input
                type="color"
                value={form.accentColor || '#1d4ed8'}
                onChange={(e) => set('accentColor', e.target.value)}
                className="h-9 w-9 cursor-pointer rounded-md border border-slate-300 p-0.5"
              />
              <span className="text-sm font-mono text-slate-600">{form.accentColor || '#1d4ed8'}</span>
              <span className="text-xs text-slate-500">Accent color for the invoice header</span>
            </div>

            <div>
              <FieldLabel htmlFor="bankDetails">Bank details</FieldLabel>
              <textarea
                id="bankDetails"
                rows={3}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                placeholder="Sort code / account number / IBAN, whatever should be printed for payment"
                value={form.bankDetails}
                onChange={(e) => set('bankDetails', e.target.value)}
              />
            </div>

            <div>
              <FieldLabel htmlFor="footerNote">Footer note</FieldLabel>
              <textarea
                id="footerNote"
                rows={2}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 shadow-sm transition-colors focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/40"
                placeholder="e.g. Payment due within 30 days of invoice date"
                value={form.footerNote}
                onChange={(e) => set('footerNote', e.target.value)}
              />
            </div>

            <div className="flex justify-end border-t border-slate-100 pt-4">
              <Button size="sm" isLoading={saveMutation.isPending} onClick={() => { setError(null); saveMutation.mutate(); }}>
                Save
              </Button>
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

// Platform-wide settings (MSP Admin only). Currently just the Oakford
// Technology logo, but this is the natural home for other platform-level
// configuration (mail settings, etc.) as it grows.
export function PlatformSettingsPage() {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const brandingQuery = useQuery({ queryKey: ['platform-branding'], queryFn: getPlatformBranding });

  const logoMutation = useMutation({
    mutationFn: (file: File) => uploadPlatformLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-branding'] });
      showToast('Oakford Technology logo updated.');
    },
    onError: (err) => setError(apiErrorMessage(err, 'Could not upload the logo.')),
  });

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null);
    logoMutation.mutate(file);
    e.target.value = '';
  }

  return (
    <div>
      <PageHeader title="Platform Settings" />

      <Card>
        <CardHeader>
          <span className="text-sm font-medium text-slate-600">
            Oakford Technology's own logo - shown on the login page and in the sidebar for every user.
          </span>
        </CardHeader>
        <CardBody className="flex flex-col gap-4">
          {error && <Alert tone="error">{error}</Alert>}

          {brandingQuery.isLoading ? (
            <div className="py-6 text-center">
              <Spinner />
            </div>
          ) : (
            <div className="flex items-center gap-4">
              <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-slate-200 bg-slate-50">
                {brandingQuery.data?.hasLogo ? (
                  <img
                    src={`${PLATFORM_LOGO_URL}?v=${encodeURIComponent(brandingQuery.data.updatedAt)}`}
                    alt="Oakford Technology logo"
                    className="h-full w-full object-contain"
                  />
                ) : (
                  <span className="text-lg font-bold text-slate-300">O</span>
                )}
              </div>
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  isLoading={logoMutation.isPending}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-3.5 w-3.5" /> Upload logo
                </Button>
                <p className="mt-1.5 text-xs text-slate-500">PNG, JPEG, WebP or SVG, up to 2MB.</p>
              </div>
            </div>
          )}
        </CardBody>
      </Card>

      <div className="mt-6">
        <InvoiceTemplateSection hasLogo={!!brandingQuery.data?.hasLogo} />
      </div>

      <div className="mt-6">
        <CategoriesSection />
      </div>

      <div className="mt-6">
        <DefaultsSection />
      </div>
    </div>
  );
}
